
import React, { useMemo } from 'react';
import { Table, TableBody } from '@/components/ui/table';
import AdminTableRow from '@/components/AdminTableRow';
import AdminPaginationControls from '@/components/AdminPaginationControls';
import { useSorting } from '@/hooks/useSorting';
import { useBehavioralSubmissionData } from '@/hooks/useBehavioralSubmissionData';
import BehavioralTableHeader from '@/components/admin/BehavioralTableHeader';
import EmptyStateMessage from '@/components/admin/EmptyStateMessage';
import IdealProfileSelector from '@/components/IdealProfileSelector';
import AdminSearchBar from '@/components/AdminSearchBar';

import AdminFilters from '@/components/AdminFilters';
import { generateProfileName } from '@/utils/profileNameGenerator';
import { Loader2 } from 'lucide-react';
import type { FilterState } from '@/types/filterTypes';

interface SurveySubmission {
  id: string;
  full_name: string;
  email: string;
  profile_name?: string | null;
  scores: any;
  selected_words: any;
  selected_words_text: any;
  completed_at: string;
  created_at: string;
  company_id?: string | null;
}

interface IdealProfile {
  id: string;
  name: string;
  dominance_min: number;
  dominance_max: number;
  extraversion_min: number;
  extraversion_max: number;
  patience_min: number;
  patience_max: number;
  formality_min: number;
  formality_max: number;
  objectivity_min: number;
  objectivity_max: number;
  company_id?: string;
}

interface AdminBehavioralTabProps {
  filteredSubmissions: SurveySubmission[];
  allSubmissions: SurveySubmission[];
  currentFilters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  filtersExpanded: boolean;
  onToggleFilters: () => void;
  onViewDetails: (submission: SurveySubmission) => void;
  onSubmissionDeleted: (id: string) => void;
  onStatusUpdate?: (submissionId: string, newStatus: string) => Promise<void>;
  pagination: {
    currentPage: number;
    pageSize: number;
    totalPages: number;
    startIndex: number;
    endIndex: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    setCurrentPage: (page: number) => void;
    setPageSize: (size: number) => void;
    goToNextPage: () => void;
    goToPreviousPage: () => void;
    resetToFirstPage: () => void;
  };
  totalCount: number;
  searchTerm: string;
  isSearching?: boolean;
  onSearchChange: (term: string) => void;
  tableExpanded: boolean;
  onToggleTable?: () => void;
  selectedIdealProfile: IdealProfile | null;
  setSelectedIdealProfile: (profile: IdealProfile | null) => void;
  profileFilter: string;
  onProfileFilterChange: (value: string) => void;
}

const AdminBehavioralTab: React.FC<AdminBehavioralTabProps> = ({
  filteredSubmissions,
  allSubmissions,
  currentFilters,
  onFiltersChange,
  onViewDetails,
  onSubmissionDeleted,
  onStatusUpdate,
  pagination,
  totalCount,
  tableExpanded,
  onToggleTable,
  selectedIdealProfile,
  filtersExpanded,
  onToggleFilters,
  setSelectedIdealProfile,
  searchTerm,
  isSearching = false,
  onSearchChange,
  profileFilter,
  onProfileFilterChange
}) => {
  const { enhancedSubmissions, isSuperAdmin } = useBehavioralSubmissionData(
    filteredSubmissions,
    selectedIdealProfile
  );

  const { sortedData, sortConfig, handleSort } = useSorting({
    data: enhancedSubmissions,
    defaultSortKey: 'completed_at',
    defaultSortDirection: 'desc'
  });

  return (
    <div className="space-y-4">
      {/* Control Bar */}
      <div className="flex flex-wrap items-center gap-3">
        <AdminSearchBar
          searchTerm={searchTerm}
          onSearchChange={onSearchChange}
        />

        <IdealProfileSelector
          selectedProfile={selectedIdealProfile}
          onProfileChange={setSelectedIdealProfile}
          compact={false}
        />
        <AdminFilters
          submissions={allSubmissions}
          filters={currentFilters}
          onFiltersChange={onFiltersChange}
          isExpanded={filtersExpanded}
          onToggleExpanded={onToggleFilters}
        />
      </div>

      {/* Table Section */}
      <div>
        {isSearching ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            <span className="ml-2 text-sm text-muted-foreground">Searching...</span>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <BehavioralTableHeader
                sortConfig={sortConfig}
                onSort={handleSort}
                isSuperAdmin={isSuperAdmin}
                selectedIdealProfile={selectedIdealProfile}
                tableExpanded={tableExpanded}
              />
                <TableBody>
                  {sortedData.map(submission => (
                    <AdminTableRow 
                      key={submission.id} 
                      submission={submission} 
                      onViewDetails={onViewDetails} 
                      onSubmissionDeleted={onSubmissionDeleted}
                      onStatusUpdate={onStatusUpdate}
                      hideProfile={true} 
                      selectedIdealProfile={selectedIdealProfile} 
                      cognitivePercentile={submission.cognitivePercentile} 
                      isExpanded={tableExpanded} 
                      showCompany={isSuperAdmin} 
                      companyName={submission.companyName} 
                      showChart={true} 
                    />
                  ))}
              </TableBody>
            </Table>
          </div>
        )}
        
        {!isSearching && sortedData.length === 0 && (
          <div className="py-12 text-center">
            <EmptyStateMessage />
          </div>
        )}
        
        {totalCount > 0 && (
          <div className="border-t border-border px-4 py-3 bg-muted/20">
            <AdminPaginationControls 
              currentPage={pagination.currentPage}
                totalPages={pagination.totalPages} 
                pageSize={pagination.pageSize} 
                totalItems={totalCount} 
                startIndex={pagination.startIndex} 
                endIndex={pagination.endIndex} 
                hasNextPage={pagination.hasNextPage} 
                hasPreviousPage={pagination.hasPreviousPage} 
                onPageChange={pagination.setCurrentPage} 
                onPageSizeChange={pagination.setPageSize} 
                onNextPage={pagination.goToNextPage} 
                onPreviousPage={pagination.goToPreviousPage} 
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBehavioralTab;
